/**
 * Elegant Case Study Template Component
 * Premium, sophisticated design with visual impact
 */

import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import {
  Users, Clock, ClipboardList, TrendingUp, Tablet,
  QrCode, Sparkles, Activity, DollarSign, Star,
  Zap, MessageCircle, BarChart, Wine, Target, Link,
  ArrowRight, ExternalLink, CheckCircle, ChevronLeft, ChevronRight,
  Quote, ChefHat, Utensils, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CaseStudy } from '@/types/caseStudy';
import { Link as RouterLink } from 'react-router-dom';

// Icon mapping for string references
const iconMap: { [key: string]: any } = {
  'users': Users,
  'clock': Clock,
  'clipboard': ClipboardList,
  'trending-up': TrendingUp,
  'tablet': Tablet,
  'qr-code': QrCode,
  'sparkles': Sparkles,
  'activity': Activity,
  'dollar-sign': DollarSign,
  'star': Star,
  'zap': Zap,
  'message-circle': MessageCircle,
  'bar-chart': BarChart,
  'wine': Wine,
  'target': Target,
  'link': Link
};

interface CaseStudyTemplateProps {
  caseStudy: CaseStudy;
}

export const CaseStudyTemplate: React.FC<CaseStudyTemplateProps> = ({ caseStudy }) => {
  const [currentSolutionIndex, setCurrentSolutionIndex] = useState(0);
  const [animatedMetrics, setAnimatedMetrics] = useState<any>({});

  const getIcon = (iconName?: string) => {
    if (!iconName) return null;
    const IconComponent = iconMap[iconName];
    return IconComponent || null;
  };

  const nextSolution = () => {
    setCurrentSolutionIndex((prev) => 
      (prev + 1) % caseStudy.solution.implementation.length
    );
  };

  const prevSolution = () => {
    setCurrentSolutionIndex((prev) => 
      prev === 0 ? caseStudy.solution.implementation.length - 1 : prev - 1
    );
  };

  // Animate numbers on scroll
  useEffect(() => {
    const handleScroll = () => {
      const resultsSection = document.getElementById('results-section');
      if (resultsSection) {
        const rect = resultsSection.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          // Trigger animation
          setAnimatedMetrics({
            serverTrips: true,
            cocktailRevenue: true,
            customerRating: true,
            processingTime: true
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#2256e5] relative overflow-hidden">
      {/* Subtle animated background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/10 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-dineai-yellow/10 blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Hero Section - Full Width Impact */}
      <section className="relative min-h-[500px] flex items-center">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="/opulent_lobby.png"
            alt="The Opulent Interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1f4ed1]/95 via-[#2256e5]/85 to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Logo and Badge */}
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src="/the-opulent-logo.png"
                  alt="The Opulent"
                  className="h-16 object-contain filter brightness-0 invert"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <div className="inline-flex items-center gap-2 bg-dineai-yellow px-4 py-2 rounded-full">
                  <Star className="w-4 h-4 text-black" />
                  <span className="text-black font-medium">Success Story</span>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                From Staffing to<br/>
                <span className="text-dineai-yellow">AI Seamless Service</span>
              </h1>
              
              <p className="text-lg text-white/90 mb-8 max-w-2xl">
                How The Opulent transformed their operations with AI, saving 15+ hours weekly 
                and increasing cocktail sales by 35%
              </p>

              {/* Key Metrics - 4 Boxes like Dine AI Impact */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
                {caseStudy.keyMetrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20"
                  >
                    <div className="text-2xl font-bold text-white mb-1">
                      {metric.value}
                    </div>
                    <div className="text-xs text-white/70">
                      {metric.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Overview - Elegant */}
      <section className="py-12 bg-[#2256e5] relative z-10">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <Quote className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-xl text-white/90 font-light italic leading-relaxed">
              {caseStudy.overview.content}
            </p>
          </div>
        </div>
      </section>

      {/* The Challenge - Visual Icons */}
      <section className="py-10 bg-[#1f4ed1] relative z-10">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.h2 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-white text-center mb-10"
            >
              The Challenge
            </motion.h2>
            
            <div className="grid md:grid-cols-4 gap-6">
              {caseStudy.challenges.items.map((challenge, index) => {
                const IconComponent = getIcon(challenge.icon);
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 }}
                    className="text-center group"
                  >
                    <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/20 group-hover:scale-110 transition-transform">
                      {IconComponent && (
                        <IconComponent className="w-10 h-10 text-dineai-yellow" />
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-white">
                      {challenge.title}
                    </h3>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* The Solution - 3 Premium Cards */}
      <section className="py-12 bg-[#2256e5] relative z-10">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.h2 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-white text-center mb-10"
            >
              The Solution
            </motion.h2>

            {/* Solution Cards Grid - AI Recommendations First */}
            <div className="grid md:grid-cols-3 gap-6">
              {[
                caseStudy.solution.implementation[2], // AI-Powered Recommendations
                caseStudy.solution.implementation[0], // Tablet Deployment
                caseStudy.solution.implementation[1]  // Table-Side Quick Scan
              ].map((solution, index) => {
                const IconComponent = getIcon(solution.icon);
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2, type: "spring" }}
                    className="group relative"
                  >
                    <div className="bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:border-dineai-yellow/50 transition-all duration-300 h-full hover:scale-105 hover:shadow-2xl">
                      {/* Icon */}
                      <div className="w-16 h-16 bg-gradient-to-br from-dineai-yellow/30 to-dineai-yellow/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        {IconComponent && (
                          <IconComponent className="w-8 h-8 text-dineai-yellow" />
                        )}
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-2xl font-bold text-white mb-3">
                        {solution.title}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-white/70 text-sm mb-6">
                        {solution.description}
                      </p>
                      
                      {/* Features */}
                      <div className="space-y-2">
                        {solution.features.map((feature, idx) => (
                          <motion.div 
                            key={idx} 
                            className="flex items-start gap-2"
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.2 + idx * 0.1 }}
                          >
                            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-white/60">{feature}</span>
                          </motion.div>
                        ))}
                      </div>
                      
                      {/* Hover Glow Effect */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-dineai-yellow/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* The Results - Visual Progress Bars */}
      <section id="results-section" className="py-12 bg-[#1f4ed1] relative z-10">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.h2 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-white text-center mb-10"
            >
              The Results
            </motion.h2>

            <div className="grid md:grid-cols-2 gap-6">
              {caseStudy.results.metrics.map((result, index) => {
                // Extract percentage or multiplier for progress bar
                const getProgress = () => {
                  if (result.improvement.includes('60%')) return 60;
                  if (result.improvement.includes('35%')) return 35;
                  if (result.improvement.includes('3x')) return 75;
                  if (result.improvement.includes('4.8')) return 96;
                  return 50;
                };
                
                const progress = getProgress();
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15, type: "spring" }}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/15 transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-semibold mb-1 text-white">{result.metric}</h3>
                        <div className="text-3xl font-bold text-dineai-yellow">
                          {result.improvement}
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar - Slimmer */}
                    <div className="mb-4">
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${progress}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.5, ease: "easeOut", delay: index * 0.2 }}
                          className="h-full bg-gradient-to-r from-dineai-yellow to-yellow-300 rounded-full"
                        />
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-300 leading-relaxed">
                      {result.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial - Two Column Layout */}
      {caseStudy.testimonials.length > 0 && (
        <section className="py-12 bg-[#2256e5] relative z-10">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="grid md:grid-cols-[300px,1fr] gap-8 items-center"
              >
                {/* Left Column - Author Info */}
                <div className="flex flex-col items-center md:items-start text-center md:text-left">
                  <div className="w-32 h-32 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border-4 border-dineai-yellow shadow-2xl mb-4">
                    <User className="w-16 h-16 text-dineai-yellow" />
                  </div>
                  <cite className="text-2xl text-white font-bold not-italic block mb-1">
                    {caseStudy.testimonials[0].author}
                  </cite>
                  <p className="text-white/70 text-lg">{caseStudy.testimonials[0].role}</p>
                </div>
                
                {/* Right Column - Quote */}
                <div className="relative">
                  <Quote className="absolute -top-4 -left-4 w-12 h-12 text-dineai-yellow/20" />
                  <blockquote className="text-xl md:text-2xl text-white/90 font-light italic leading-relaxed pl-8">
                    {caseStudy.testimonials[0].quote}
                  </blockquote>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Features - Clean Grid */}
      <section className="py-10 bg-[#1f4ed1] relative z-10">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.h2 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-white text-center mb-10"
            >
              Powered by Dine AI
            </motion.h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {caseStudy.featuresUsed.features.map((feature, index) => {
                const IconComponent = getIcon(feature.icon);
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:border-dineai-yellow/30 transition-all group"
                  >
                    <div className="flex flex-col items-center text-center">
                      {IconComponent && (
                        <div className="w-16 h-16 bg-gradient-to-br from-dineai-yellow/20 to-dineai-yellow/10 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                          <IconComponent className="w-8 h-8 text-dineai-yellow" />
                        </div>
                      )}
                      <h3 className="text-lg font-bold text-white mb-2">
                        {feature.name}
                      </h3>
                      <p className="text-sm text-white/60">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action - Premium Feel */}
      <section className="py-16 bg-[#2256e5] relative z-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Ready to Transform Your Restaurant?
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Join The Opulent and start saving 15+ hours every week
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <RouterLink to="/restaurant-menu-search">
                <Button className="bg-gradient-to-r from-dineai-yellow to-yellow-400 text-black hover:from-yellow-400 hover:to-dineai-yellow text-lg py-7 px-12 rounded-full shadow-2xl transform hover:scale-105 transition-all font-bold">
                  Start Your Free Trial
                  <ArrowRight className="ml-3 w-6 h-6" />
                </Button>
              </RouterLink>
              <RouterLink to="/dancing-queen">
                <Button className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] text-lg py-7 px-12 rounded-full transform hover:scale-105 transition-all flex items-center gap-2">
                  WATCH DEMO
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </RouterLink>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};